import React from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * Unified claim-rejection display — ONE consistent treatment no matter
 * which pipe delivered the rejection:
 *   • 277CA (X12) — clearinghouse/payer front-end edits
 *   • TriZetto/NEHEN CSR — "rejected" / "clearinghouse_rejected" (TriZetto
 *     sends plain "rejected") / "payer_rejected"
 * Reads claim.rejection_reason (canonical), falling back to
 * needs_corrections_reason / denial_info.denial_reason.
 */
export const getClaimRejection = (claim) => {
  if (!claim) return null;
  const csr = claim.csr_last_status;
  // Only claims whose CURRENT status is rejected — once a claim is
  // corrected/resubmitted (billed/submitted/paid), stale CSR rejection
  // pills must not keep showing a rejection banner.
  const isRejected = claim.status === 'rejected';
  if (!isRejected) return null;
  const reason =
    claim.rejection_reason ||
    claim.needs_corrections_reason ||
    claim.denial_info?.denial_reason;
  if (!reason) return null;

  const src = claim.rejection_source;
  let label;
  if (src === 'csr_payer' || csr === 'payer_rejected') label = 'Payer rejected';
  else if (src === 'csr_clearinghouse' || csr === 'clearinghouse_rejected' || csr === 'rejected') label = 'Clearinghouse rejected';
  else if (src === '277ca' || claim.ack_277_status) label = 'Rejected (277CA)';
  else label = 'Rejected';
  const at = claim.rejection_at || claim.ack_277_received_at || claim.csr_last_seen_at;
  return { reason, label, at };
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
};

export const ClaimRejectionNotice = ({ claim, variant = 'inline', className = '' }) => {
  const rej = getClaimRejection(claim);
  if (!rej) return null;

  if (variant === 'banner') {
    return (
      <div
        className={`p-3 bg-red-50 border border-red-200 rounded-lg ${className}`}
        data-testid={`claim-rejection-banner-${claim.id}`}
      >
        <div className="flex items-center gap-2 mb-1">
          <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
          <span className="text-sm font-semibold text-red-800">{rej.label}</span>
          {fmtDate(rej.at) && <span className="text-xs text-red-500">· {fmtDate(rej.at)}</span>}
        </div>
        <div className="text-xs text-gray-700">{rej.reason}</div>
      </div>
    );
  }

  return (
    <div
      className={`mt-1 flex items-start gap-1 max-w-xs ${className}`}
      title={`${rej.label}: ${rej.reason}`}
      data-testid={`claim-rejection-inline-${claim.id}`}
    >
      <span className="shrink-0 px-1 py-0 text-[9px] font-semibold uppercase tracking-wide rounded bg-red-100 text-red-700 border border-red-200">
        {rej.label}
      </span>
      <span className="text-[10px] text-red-600 italic truncate">{rej.reason}</span>
    </div>
  );
};
